import React, { useState, useEffect } from "react";
import {
  PencilIcon,
  PlusIcon,
  CheckIcon,
  XIcon,
  TrashIcon,
} from "lucide-react";
import { db } from "../../config/firebaseConfig";
import { ref, set, get } from "firebase/database";
import { useToast } from "../../@/components/ui/use-toast";
import SortupDialog from "../../components/common/SortupDialog";
import LayoutSkeleton from "../../components/skeletons/LayoutSkeleton";

interface AboutSection {
  title: string;
  content: string;
}

const EditAboutUs = () => {
  // State for storing about us content
  const [aboutUsData, setAboutUsData] = useState({
    intro: "",
    sections: [] as AboutSection[],
  });
  const { toast } = useToast();
  // State for tracking which section is being edited
  const [editingSection, setEditingSection] = useState<string | null>(null);
  // State for tracking which custom section is being edited
  const [editingCustomIndex, setEditingCustomIndex] = useState<number | null>(
    null
  );
  // State for adding new section
  const [isAddingNew, setIsAddingNew] = useState(false);
  // Temporary state for editing
  const [editText, setEditText] = useState("");
  const [editTitle, setEditTitle] = useState("");
  // Loading state
  const [loading, setLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState<number | null>(null);

  // Fetch data from Firebase on component mount
  useEffect(() => {
    const fetchAboutUsData = async () => {
      try {
        const aboutUsRef = ref(db, "LayoutSettingData/AboutUs");
        const snapshot = await get(aboutUsRef);

        if (snapshot.exists()) {
          const data = snapshot.val();
          // Ensure sections array exists
          if (!data.sections) {
            data.sections = [
              { title: "Our Mission", content: data.ourMission || "" },
              { title: "Our Story", content: data.ourStory || "" },
              {
                title: "Why Choose Afrocart?",
                content: data.whyChooseUs || "",
              },
              { title: "Our Vision", content: data.ourVision || "" },
              { title: "Get Involved", content: data.getInvolved || "" },
            ];

            // Save the new format
            await saveToFirebase(data);
          }
          setAboutUsData(data);
        } else {
          // If no data exists, initialize with current content
          const initialData = {
            intro:
              "Welcome to Afrocart, your go-to platform for all African groceries and catering needs. We specialize in providing a diverse selection of authentic African products and services to our customers across Canada.",
            sections: [
              {
                title: "Our Mission",
                content:
                  "At Afrocart, our mission is to provide high-quality African grocery and catering products with exceptional service to households and businesses. We focus on delivering authentic flavors, offering competitive pricing, and ensuring fast delivery.",
              },
              {
                title: "Our Story",
                content:
                  "Afrocart was founded to connect African grocery stores and consumers across different regions. We aim to be the trusted partner for both individual shoppers and catering businesses seeking quality African products.",
              },
              {
                title: "Why Choose Afrocart?",
                content:
                  "- Authentic variety of African groceries and catering options \n- Fast and reliable delivery services \n- Competitive pricing tailored for individuals and businesses \n- Dedicated support for African catering businesses \n- User-friendly platform with seamless online integration",
              },
              {
                title: "Our Vision",
                content:
                  "We aim to continue expanding our market reach, bringing authentic African groceries and catering services to more communities. Our commitment to quality, affordability, and excellent customer service remains at the heart of everything we do.",
              },
              {
                title: "Get Involved",
                content:
                  "Interested in partnering with Afrocart or becoming a supplier? Contact us at admin@afrocart.ca to learn more and become part of our growing community.",
              },
            ],
          };
          setAboutUsData(initialData);
          await saveToFirebase(initialData);
        }
      } catch (error) {
        console.error("Error fetching about us data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAboutUsData();
  }, []);

  // Save data to Firebase
  const saveToFirebase = async (data = aboutUsData) => {
    try {
      const aboutUsRef = ref(db, "LayoutSettingData/AboutUs");
      await set(aboutUsRef, data);
      return true;
    } catch (error) {
      console.error("Error saving about us data:", error);
      return false;
    }
  };

  // Handle edit button click
  const handleEdit = (section: string, content: string) => {
    setEditingSection(section);
    setEditText(content);
  };

  // Handle edit custom section
  const handleEditCustom = (index: number) => {
    setEditingCustomIndex(index);
    setEditTitle(aboutUsData.sections[index].title);
    setEditText(aboutUsData.sections[index].content);
  };

  // Handle add new section
  const handleAddNew = () => {
    setIsAddingNew(true);
    setEditTitle("");
    setEditText("");
  };

  // Handle save edit
  const handleSave = async () => {
    if (editingSection === "intro") {
      const newData = { ...aboutUsData, intro: editText };
      const success = await saveToFirebase(newData);
      if (success) {
        setAboutUsData(newData);
        setEditingSection(null);
        toast({
          title: "Success",
          description: "About us content updated successfully",
          showIcon: true,
          variant: "success",
        });
      }
    } else if (editingCustomIndex !== null) {
      const newSections = [...aboutUsData.sections];
      newSections[editingCustomIndex] = {
        title: editTitle,
        content: editText,
      };
      const newData = { ...aboutUsData, sections: newSections };
      const success = await saveToFirebase(newData);
      if (success) {
        setAboutUsData(newData);
        setEditingCustomIndex(null);
        toast({
          title: "Success",
          description: "About us content updated successfully",
          showIcon: true,
          variant: "success",
        });
      }
    }
  };

  // Handle save new section
  const handleSaveNew = async () => {
    if (editTitle.trim() === "" || editText.trim() === "") {
      toast({
        title: "Error",
        description: "Please enter both title and content for the new section",
        variant: "destructive",
        showIcon: true,
      });
      return;
    }

    const newSection = {
      title: editTitle,
      content: editText,
    };

    const newData = {
      ...aboutUsData,
      sections: [...aboutUsData.sections, newSection],
    };

    const success = await saveToFirebase(newData);
    if (success) {
      setAboutUsData(newData);
      setIsAddingNew(false);
      setEditTitle("");
      setEditText("");
      toast({
        title: "Success",
        description: "New section added successfully",
        variant: "success",
        showIcon: true,
      });
    } else {
      toast({
        title: "Error",
        description: "Failed to add new section",
        variant: "destructive",
        showIcon: true,
      });
    }
  };

  // Handle delete section
  const handleDelete = async (index: number) => {
    setSectionToDelete(index);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (sectionToDelete !== null) {
      const newSections = aboutUsData.sections.filter(
        (_, i) => i !== sectionToDelete
      );
      const newData = { ...aboutUsData, sections: newSections };
      const success = await saveToFirebase(newData);
      if (success) {
        setAboutUsData(newData);
        toast({
          title: "Success",
          description: "Section deleted successfully",
          variant: "success",
          showIcon: true,
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to delete section",
          variant: "destructive",
          showIcon: true,
        });
      }
    }
    setShowDeleteDialog(false);
    setSectionToDelete(null);
  };

  // Handle cancel edit
  const handleCancel = () => {
    setEditingSection(null);
    setEditingCustomIndex(null);
    setIsAddingNew(false);
    setEditText("");
    setEditTitle("");
  };

  // Render a section
  const renderSection = (title: string, content: string, section: string) => {
    return (
      <div className="w-full">
        {title && <h2 className="font-helveticaBold text-2xl mb-3">{title}</h2>}

        {editingSection === section ? (
          <div className="mt-3">
            <textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-md min-h-[200px] focus:ring-2 focus:ring-customGreenColor focus:border-transparent"
            />
            <div className="flex mt-3 space-x-3">
              <button
                onClick={handleSave}
                className="bg-customGreenColor hover:bg-opacity-90 transition-all text-white px-4 py-2 rounded-md flex items-center"
              >
                <CheckIcon className="h-4 w-4 mr-2" /> Save
              </button>
              <button
                onClick={handleCancel}
                className="bg-gray-200 hover:bg-gray-300 transition-all text-gray-700 px-4 py-2 rounded-md flex items-center"
              >
                <XIcon className="h-4 w-4 mr-2" /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex justify-between items-start">
            <div className="flex-grow">
              <p
                className={
                  section === "whyChooseUs"
                    ? "whitespace-pre-line"
                    : "leading-relaxed"
                }
              >
                {content}
              </p>
            </div>
            <button
              className="text-customGreenColor hover:text-opacity-80 transition-all ml-3 flex-shrink-0"
              onClick={() => handleEdit(section, content)}
            >
              <PencilIcon className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>
    );
  };

  // Render a custom section
  const renderCustomSection = (section: AboutSection, index: number) => {
    return (
      <div key={index} className="bg-gray-50 rounded-lg p-6 shadow-sm">
        {editingCustomIndex === index ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Section Title
              </label>
              <input
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-customGreenColor focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Content
              </label>
              <textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md min-h-[200px] focus:ring-2 focus:ring-customGreenColor focus:border-transparent"
              />
            </div>
            <div className="flex space-x-3 pt-2">
              <button
                onClick={handleSave}
                className="bg-customGreenColor hover:bg-opacity-90 transition-all text-white px-4 py-2 rounded-md flex items-center"
              >
                <CheckIcon className="h-4 w-4 mr-2" /> Save
              </button>
              <button
                onClick={handleCancel}
                className="bg-gray-200 hover:bg-gray-300 transition-all text-gray-700 px-4 py-2 rounded-md flex items-center"
              >
                <XIcon className="h-4 w-4 mr-2" /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-helveticaBold text-2xl">{section.title}</h2>
              <div className="flex space-x-3">
                <button
                  className="text-customGreenColor hover:text-opacity-80 transition-all"
                  onClick={() => handleEditCustom(index)}
                >
                  <PencilIcon className="h-5 w-5" />
                </button>
                <button
                  className="text-red-500 hover:text-opacity-80 transition-all"
                  onClick={() => handleDelete(index)}
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
            <p className="whitespace-pre-line leading-relaxed">
              {section.content}
            </p>
          </>
        )}
      </div>
    );
  };

  if (loading) {
    return <LayoutSkeleton />;
  }

  return (
    <div className="max-w-[1400px] mx-auto bg-white p-6">
      <div className="w-full flex flex-col font-normal text-sm space-y-6 text-black">
        <div className="flex gap-4 flex-col md:flex-row justify-between items-center w-full mb-8">
          <h1 className="font-helveticaBold text-3xl md:text-4xl text-black">
            About Afrocart
          </h1>
          <button
            className="text-white w-full md:w-auto bg-customGreenColor justify-center hover:bg-opacity-90 transition-all px-4 py-2 rounded-md flex items-center"
            onClick={handleAddNew}
          >
            <PlusIcon className="h-4 w-4 mr-2" /> Add Section
          </button>
        </div>

        {/* Intro section */}
        <div className="bg-gray-50 rounded-lg p-6">
          {renderSection("", aboutUsData.intro, "intro")}
        </div>

        {/* Add New Section Form */}
        {isAddingNew && (
          <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
            <h3 className="font-helveticaBold text-black text-xl mb-4">
              Add New Section
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Section Title
                </label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-customGreenColor focus:border-transparent"
                  placeholder="Enter section title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Content
                </label>
                <textarea
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-md min-h-[200px] focus:ring-2 focus:ring-customGreenColor focus:border-transparent"
                  placeholder="Enter section content"
                />
              </div>
              <div className="flex space-x-3 pt-2">
                <button
                  onClick={handleSaveNew}
                  className="bg-customGreenColor hover:bg-opacity-90 transition-all text-white px-4 py-2 rounded-md flex items-center"
                >
                  <CheckIcon className="h-4 w-4 mr-2" /> Save
                </button>
                <button
                  onClick={handleCancel}
                  className="bg-gray-200 hover:bg-gray-300 transition-all text-gray-700 px-4 py-2 rounded-md flex items-center"
                >
                  <XIcon className="h-4 w-4 mr-2" /> Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Custom Sections */}
        <div className="grid gap-6">
          {aboutUsData.sections.map((section, index) =>
            renderCustomSection(section, index)
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <SortupDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setSectionToDelete(null);
        }}
        header="Confirm Delete"
        footerButtons={[
          {
            name: "Delete",
            onClick: confirmDelete,
            disabled: false,
            className: "bg-customGreenColor text-white hover:bg-opacity-90",
          },
          {
            name: "Cancel",
            onClick: () => {
              setShowDeleteDialog(false);
              setSectionToDelete(null);
            },
            disabled: false,
            className: "bg-gray-200 text-gray-700 hover:bg-gray-300",
          },
        ]}
      >
        <p>
          Are you sure you want to delete this section? This action cannot be
          undone.
        </p>
      </SortupDialog>
    </div>
  );
};

export default EditAboutUs;
